import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData, createSVGElement } from "../../../svg/";
import { YZCanvasContext } from "../canvas";
import { CubicBezier } from "../line/";

export type RectShapeOptions = {
  rectWidth?: number;
  rectHeight?: number;
  rectBorderRadius?: number; // x y rectWidth rectHeight 包含 rectBorderRadius
} & ShapeOptions;

export class RectShape extends Shape {
  rectWidth: number;
  rectHeight: number;
  rectBorderRadius: number;

  constructor(options: RectShapeOptions) {
    super(options);
    this.rectWidth = options.rectWidth || 0;
    this.rectHeight = options.rectHeight || 0;
    this.rectBorderRadius = options.rectBorderRadius || 0;
  }

  protected cloneShape(): RectShape {
    const shape = new RectShape({
      ...super.getBaseOptions(),
      rectWidth: this.rectWidth,
      rectHeight: this.rectHeight,
      rectBorderRadius: this.rectBorderRadius
    });
    return shape;
  }

  // 供外部使用：基本属性变更
  public update(options: Partial<RectShapeOptions>) {
    if (typeof options.rectWidth == "number") {
      this.rectWidth = options.rectWidth;
      this.shouldUpdatePathD = true;
    }
    if (typeof options.rectHeight == "number") {
      this.rectHeight = options.rectHeight;
      this.shouldUpdatePathD = true;
    }
    if (typeof options.rectBorderRadius == "number") {
      this.rectBorderRadius = options.rectBorderRadius;
      this.shouldUpdatePathD = true;
    }
  }

  public getBoundingClientRect() {
    return {
      x: 0,
      y: 0,
      width: this.rectWidth,
      height: this.rectHeight
    };
  }

  public toSVGEl(): SVGElement | null {
    const el = createSVGElement("rect");
    this.setAttr({
      x: "0",
      y: "0",
      width: String(this.rectWidth),
      height: String(this.rectHeight),
      rx: String(this.rectBorderRadius),
      ry: String(this.rectBorderRadius)
    });
    return el;
  }

  public toSVGPathData(): SVGParsedPathData[] {
    if (!this.rectBorderRadius) {
      return [
        ["m", 0, 0],
        ["l", this.rectWidth, 0],
        ["l", 0, this.rectHeight],
        ["l", -this.rectWidth, 0],
        ["z"]
      ];
    } else {
      return [
        ["m", this.rectBorderRadius, 0],
        ["l", this.rectWidth - this.rectBorderRadius * 2, 0],
        [
          "a",
          this.rectBorderRadius,
          this.rectBorderRadius,
          0,
          0,
          1,
          this.rectBorderRadius,
          this.rectBorderRadius
        ],
        ["l", 0, this.rectHeight - this.rectBorderRadius * 2],
        [
          "a",
          this.rectBorderRadius,
          this.rectBorderRadius,
          0,
          0,
          1,
          -this.rectBorderRadius,
          this.rectBorderRadius
        ],
        ["l", this.rectBorderRadius * 2 - this.rectWidth, 0],
        [
          "a",
          this.rectBorderRadius,
          this.rectBorderRadius,
          0,
          0,
          1,
          -this.rectBorderRadius,
          -this.rectBorderRadius
        ],
        ["l", 0, this.rectBorderRadius * 2 - this.rectHeight],
        [
          "a",
          this.rectBorderRadius,
          this.rectBorderRadius,
          0,
          0,
          1,
          this.rectBorderRadius,
          -this.rectBorderRadius
        ],
        ["z"]
      ];
    }
  }

  public toCanvasContext(ctx: YZCanvasContext) {
    ctx.beginPath();
    if (this.rectBorderRadius <= 0) {
      ctx.rect(0, 0, this.rectWidth, this.rectHeight);
    } else {
      ctx.moveTo(0, this.rectBorderRadius);
      ctx.lineTo(0, this.rectHeight - this.rectBorderRadius);
      ctx.quadraticCurveTo(
        0,
        this.rectHeight,
        this.rectBorderRadius,
        this.rectHeight
      );
      ctx.lineTo(this.rectWidth - this.rectBorderRadius, this.rectHeight);
      ctx.quadraticCurveTo(
        this.rectWidth,
        this.rectHeight,
        this.rectWidth,
        this.rectHeight - this.rectBorderRadius
      );
      ctx.lineTo(this.rectWidth, this.rectBorderRadius);
      ctx.quadraticCurveTo(
        this.rectWidth,
        0,
        this.rectWidth - this.rectBorderRadius,
        0
      );
      ctx.lineTo(this.rectBorderRadius, 0);
      ctx.quadraticCurveTo(0, 0, 0, this.rectBorderRadius);
    }
    ctx.closePath();
  }

  public static fromCubicBezier(
    p1x: number,
    p1y: number,
    c1x: number,
    c1y: number,
    c2x: number,
    c2y: number,
    p2x: number,
    p2y: number
  ): RectShape {
    const cubicBezier = new CubicBezier({
      x: p1x,
      y: p1y,
      c1x,
      c1y,
      c2x,
      c2y,
      toX: p2x,
      toY: p2y
    });
    const rect = cubicBezier.getBoundingClientRect();
    return new RectShape({
      x: rect.x,
      y: rect.y,
      rectWidth: rect.width,
      rectHeight: rect.height
    });
  }

  public eq(rect: RectShape): boolean {
    return (
      this.x === rect.x &&
      this.y === rect.y &&
      this.rectWidth === rect.rectWidth &&
      this.rectHeight === rect.rectHeight &&
      this.rectBorderRadius === rect.rectBorderRadius
    );
  }

  public intersection(rect: RectShape): boolean {
    return !(
      this.x + this.rectWidth < rect.x ||
      rect.x + rect.rectWidth < this.x ||
      this.y + this.rectHeight < rect.y ||
      rect.y + rect.rectHeight < this.y
    );
  }

  public isPointInShape(p: { x: number; y: number }) {
    return (
      p.x >= this.x &&
      p.x <= this.x + this.rectWidth &&
      p.y >= this.y &&
      p.y <= this.y + this.rectHeight
    );
  }
}

export default RectShape;
